/* eslint-disable no-loop-func */
import { createSelector } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { depositTransaction, pensionTransaction } from '@monestry-dev/schema';
import * as globals from '..';
import i18n from '../../../../i18n';

dayjs.extend(utc);

const selectorDebugEnabled = !!process.env.REACT_APP_SELECTOR_DEBUG;

function findFirstValidQuote(quotes, date) {
  if (!quotes) return null;
  const entries = Object.entries(quotes);
  if (entries.length === 0) return null;
  if (date === undefined || date === null) return entries[0][1];
  for (let i = 0; i < entries.length; i += 1) {
    const entryDate = Number(entries[i][0]);
    if (!Number.isNaN(entryDate) && entryDate > date) return entries[i][1];
  }
  return null;
}

// create a single purchase transaction for each account
export function createTransactions(accounts, quotes) {
  const returnTransactions = [];

  // for each account construct single purchase transactions with quantity of 1
  accounts.forEach((account) => {
    const { id, productPurchaseDate, currency } = account;

    // find the oldest quote for the account and use it as purchase's upac and upbc
    const firstValidQuote = findFirstValidQuote(quotes[account.id], null); // accountId = assetId for pensions
    const { quote, quoteBaseCurrency } = firstValidQuote || {};

    const purchaseTransaction = {
      id: nanoid(),
      accountId: id,
      assetId: id,
      category: 'pension',
      date: productPurchaseDate,
      quantity: 1, // always 1 (one product per account)
      quantityOpen: 0, // we don't do FIFO on pension accounts
      upac: quote || 0,
      accountCurrency: currency,
      assetCurrency: currency,
      upbc: quoteBaseCurrency || 0,
      uptc: null, // we don't do fx on pension accounts
      transactionCurrency: null, // we don't do fx on pension accounts
      isSimulated: false,
      projectId: null,
      linkedTransactions: [],
      tags: null,
      sortingOrderWithinMonth: null,
      description: `Pension purchase on ${dayjs.utc(productPurchaseDate).format('YYYY-MM-DD')}`,
      label: 'pension-purchase',
      isNotKpiRelevant: false,
    };

    returnTransactions.push(pensionTransaction.cast(purchaseTransaction));
  });
  return returnTransactions;
}

function parseTags(tags) {
  if (!tags) return {};
  if (typeof tags === 'object') return tags;
  try {
    return JSON.parse(tags);
  } catch (error) {
    if (selectorDebugEnabled) console.warn('pensionCashflowTransactions: could not parse transaction tags', tags, error);
    return {};
  }
}

export function filterPensionCashflowTransactions(transactions = [], pensionAccountIds = []) {
  const accountIdSet = new Set(pensionAccountIds);
  return (transactions || []).filter((transaction) => {
    if (!['investment-contribution', 'investment-pension'].includes(transaction.label)) return false;
    const tags = parseTags(transaction.tags);
    return accountIdSet.has(tags.accountId) || accountIdSet.has(tags.assetId);
  });
}

export const pensionCashflowTransactions = createSelector(
  (state) => state.data?.deposits.transactions,
  (state) => state.data?.pension.accounts,
  (transactions, accounts) => filterPensionCashflowTransactions(transactions, (accounts || []).map((account) => account.id)),
);

// creates simulated contributions up to endDate or payoutPhaseStart
// creates simulated payouts after payoutPhaseStart up to endDate or payoutPhaseStart + payoutsDuration in months
export function addSimulatedContributionsAndPayouts(accounts = [], quotes = {}, endDate, inflationRate) {
  const returnTransactions = [];
  const monthlyInflationFactor = inflationRate ? 1 + inflationRate / 100 / 12 : 1;

  function indexByRate(amount, baseDate, targetDate, monthlyFactor) {
    if (!monthlyFactor || monthlyFactor === 1) return amount;
    const monthsDiff = Math.max(0, dayjs.utc(targetDate).startOf('month').diff(dayjs.utc(baseDate).startOf('month'), 'month'));
    if (monthsDiff === 0) return amount;
    return amount * monthlyFactor ** monthsDiff;
  }

  // for each account construct a purchase transaction with quantity representing Present Value
  accounts.forEach((account) => {
    // get from quotes, if not available, then from account.initialQuotes
    const { quote, quoteBaseCurrency } = findFirstValidQuote(quotes[account.currency], null) || {};
    const { contributionsIndexed, payoutsIndexed } = account;

    // 251125 this was 1 / currentQBC and not just currentQBC
    // const upbc = (currentQBC && currentQBC !== 0 ? currentQBC : undefined) || account.initialQuotes.find((q) => q.type === account.useSpecialQuote)?.quoteBaseCurrency || 0;
    // THESE ARE CURRENT PAYOUTS (the current payout was already put into quote | quoteBaseCurrency by globalQuotesView)
    const upbc = quoteBaseCurrency || account.initialQuotes.find((q) => q.type === account.useSpecialQuote)?.quoteBaseCurrency || 0;
    const upac = quote || account.initialQuotes.find((q) => q.type === account.useSpecialQuote)?.quote || 0;

    const connectedDepositAccountId = (account.connectedDepositAccounts || []).find(Boolean);

    // Cashflow transactions live on the connected deposit account. Without one there is
    // no canonical account where the contribution/payout can be represented.
    if (!connectedDepositAccountId) {
      console.warn('addSimulatedContributionsAndPayouts: no connectedDepositAccounts defined for account', account.name);
      return;
    }

    const templateTransaction = {
      id: nanoid(),
      accountId: connectedDepositAccountId,
      assetId: account.currency,
      category: 'deposits',
      upac,
      accountCurrency: account.currency,
      assetCurrency: account.currency,
      upbc,
      uptc: null, // we don't do fx on pension accounts
      transactionCurrency: null, // we don't do fx on pension accounts
      isSimulated: true,
      projectId: null,
      linkedTransactions: [],
      tags: {
        accountId: account.id,
        assetId: account.id,
      },
      sortingOrderWithinMonth: null,
      otherParty: account.name,
    };

    // contribution transactions
    if (account.contributionsSelfPaid) {
      let iterDate = dayjs.utc().add(1, 'month').startOf('month').valueOf(); // start today
      const sortedContributions = account.contributionsAmounts.sort((a, b) => b.effectiveDate - a.effectiveDate); // DESC
      const contributionFrequencyMonths = Number(account.contributionsFrequency);
      const validContributionFrequency = Number.isFinite(contributionFrequencyMonths) && contributionFrequencyMonths > 0;

      // precalculate discount rate for contributions for the loop below
      const customIndexRate = Number(account.customDiscountRate);
      const monthlyCustomIndexFactor = Number.isFinite(customIndexRate) ? 1 + customIndexRate / 100 / 12 : 1;
      const contributionIndexFactor = account.discountRateIsInflation === false ? monthlyCustomIndexFactor : monthlyInflationFactor;

      let contributionLoopCounter = 0;
      while (iterDate < account.payoutsPhaseStart) {
        const contribution = sortedContributions.filter((c) => c.effectiveDate <= iterDate)[0];
        // only do indexing if account.contributionsIndexed is true
        const maybeIndexedContribution = account.contributionsIndexed
          ? indexByRate(Math.abs(contribution?.value || 0), contribution?.effectiveDate || iterDate, iterDate, contributionIndexFactor)
          : contribution?.value || 0;
        const contributionTransaction = {
          ...templateTransaction,
          date: iterDate,
          // eslint-disable-next-line no-loop-func
          quantity: -Math.abs(maybeIndexedContribution), // contributions are always negative
          description: `${i18n.language === 'de' ? 'Beitrag' : 'Contribution'} ${dayjs.utc(iterDate).format('YYYY-MM')}`,
          label: 'investment-contribution',
        };

        returnTransactions.push(depositTransaction.cast(contributionTransaction));
        if (!validContributionFrequency) break; // one-time or invalid frequency -> emit one item and stop
        iterDate = dayjs.utc(iterDate).add(contributionFrequencyMonths, 'month').startOf('month').valueOf();
        contributionLoopCounter += 1;
        if (contributionLoopCounter > 10000) {
          if (selectorDebugEnabled) console.error('addSimulatedContributionsAndPayouts: contribution loop limit reached, aborting loop', account.id);
          break;
        }
      }
    }

    // payout transactions
    if (account.payoutsPhaseStart < endDate) {
      let iterDate = dayjs.utc(account.payoutsPhaseStart).startOf('month').valueOf();
      // payoutsDuration of 0 is indefinite
      const payoutsDuration = account.payoutsDuration === 0 ? Number.POSITIVE_INFINITY : dayjs.utc(account.payoutsPhaseStart).add(account.payoutsDuration, 'month').startOf('month').valueOf();
      const payoutsFrequencyMonths = Number(account.payoutsFrequency);
      const validPayoutsFrequency = Number.isFinite(payoutsFrequencyMonths) && payoutsFrequencyMonths > 0;

      // precalculate payout discount rate
      const customIndexRate = Number(account.customDiscountRate);
      const monthlyCustomIndexFactor = Number.isFinite(customIndexRate) ? 1 + customIndexRate / 100 / 12 : 1;
      const payoutsIndexFactor = account.discountRateIsInflation === false ? monthlyCustomIndexFactor : monthlyInflationFactor;

      // incl. endDate, because when user moves the slider, we want to show the payout for the month of the slider date
      // excl. payoutDuration, because the user expects 12 payments with payment duration of 12 months, and we start in month 0
      let payoutsLoopCounter = 0;
      while (iterDate <= endDate && iterDate < payoutsDuration) {
        const payoutTransaction = {
          ...templateTransaction,
          date: iterDate,
          quantity: 1,
          upac: payoutsIndexed ? indexByRate(upac, account.payoutsPhaseStart, iterDate, payoutsIndexFactor) : upac,
          upbc: payoutsIndexed ? indexByRate(upbc, account.payoutsPhaseStart, iterDate, payoutsIndexFactor) : upbc,
          description: `${i18n.language === 'de' ? 'Auszahlung' : 'Payout'} ${dayjs.utc(iterDate).format('YYYY-MM')}`,
          label: 'investment-pension',
        };

        returnTransactions.push(depositTransaction.cast(payoutTransaction));
        if (!validPayoutsFrequency) break; // one-time or invalid frequency -> emit one item and stop
        iterDate = dayjs.utc(iterDate).add(payoutsFrequencyMonths, 'month').startOf('month').valueOf();
        payoutsLoopCounter += 1;
        if (payoutsLoopCounter > 10000) {
          if (selectorDebugEnabled) console.error('addSimulatedContributionsAndPayouts: payout loop limit reached, aborting loop', account.id);
          break;
        }
      }
    }
  });

  return returnTransactions;
}

// returns an array with all transactions (actual or actual+simulated) up to the Slider Date (baseDate) or to today (depending on dashboardMode in state)
// adds a purchase transaction for each account with correctly calculated quantity (representing Present Value)
// calculates quantityOpen with applyFIFO  (applyFIFO only works with sold items, which we don't really expect here)
// makes all transactions have isNotKpiRelevant set to true (expcept the purchase transaction)
// no isolated transactions are included
export const pensionTransactions = createSelector(
  (state) => state.data?.pension.accounts,
  globals.globalQuotesView,
  (accounts, quotes) => createTransactions(accounts || [], quotes || {}),
);

// returns an array with all transactions (actual or actual+simulated) up to the Simulation End Date (slider end)
// adds a purchase transaction for each account with correctly calculated quantity (representing Present Value)
// calculates quantityOpen with applyFIFO  (applyFIFO only works with sold items, which we don't really expect here)
// makes all transactions have isNotKpiRelevant set to true (expcept the purchase transaction)
// including all isolated transactions
export const pensionTransactionsProjectView = createSelector(
  (state) => state.data?.pension.accounts,
  globals.globalQuotesView,
  (accounts, quotes) => createTransactions(accounts || [], quotes || {}),
);
